/**
 * Global mount point for the Attach-Invoice modal.
 *
 * Always mounted at the app root next to `<TransactionModalRoot/>`. The
 * underlying `PopupModal` already uses `destroyOnHidden`, so the form
 * subtree only exists while the modal is open — no memory cost when idle.
 *
 * Wiring (mirrors `transaction-modal-root.tsx`):
 *   - Subscribes to `useAttachInvoiceModal` (shared store).
 *   - Casts the opaque `payload.launch` back to `AttachInvoiceLaunchPayload`
 *     at the boundary — `widgets → features` is legal in FSD, but the
 *     shared store side keeps the type opaque to avoid the inverse.
 *   - Intercepts `onSaved` to fire the per-open invalidation contract:
 *       1. `queryClient.invalidateQueries` for each prefix in
 *          `invalidateOnSuccess` (caller-specified — for derived queries
 *          the calling page owns).
 *       2. Caller's `onSuccess()` callback (non-query side effects:
 *          navigation, analytics, etc.).
 *     CANONICAL accounting invalidation (`/v1/accounting` prefix) is
 *     already handled inside `useCreateInvoice` — this layer adds the
 *     cross-feature derived-data refresh.
 */

import { AttachInvoiceModal } from '@/features/accounting/ui/components/pending'
import { useAttachInvoiceModal, type AttachInvoiceLaunchPayload } from '@/shared/stores'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

export function AttachInvoiceModalRoot() {
  const queryClient = useQueryClient()
  const { isOpen, payload, close } = useAttachInvoiceModal()

  const handleSaved = useCallback(() => {
    if (payload?.invalidateOnSuccess) {
      for (const prefix of payload.invalidateOnSuccess) {
        const queryKey = Array.isArray(prefix) ? prefix : [prefix]
        queryClient.invalidateQueries({ queryKey })
      }
    }
    payload?.onSuccess?.()
  }, [payload, queryClient])

  // payload.launch is structurally an AttachInvoiceLaunchPayload — see the
  // store contract. Cast at this widget boundary because `shared/` can't
  // import the feature-owned type itself.
  const launch = (payload?.launch ?? null) as AttachInvoiceLaunchPayload | null

  return <AttachInvoiceModal open={isOpen} launch={launch} onClose={close} onSaved={handleSaved} />
}
